import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

interface AnonymousParticipation {
  order: number;
  displayName: string;
  storyId: string;
}

interface AnonymousState {
  // Global identity
  anonymousId: string;
  displayName: string;

  // Per-story participation indexed by story code
  participations: Record<string, AnonymousParticipation>;

  // Actions
  setDisplayName: (name: string) => void;
  getOrCreateAnonymousId: () => string;
  setParticipation: (code: string, data: AnonymousParticipation) => void;
  getParticipation: (code: string) => AnonymousParticipation | null;
  clearParticipation: (code: string) => void;
}

export const useAnonymousStore = create<AnonymousState>()(
  persist(
    (set, get) => ({
      anonymousId: '',
      displayName: '',
      participations: {},

      setDisplayName: (name) => set({ displayName: name }),

      getOrCreateAnonymousId: () => {
        const current = get().anonymousId;
        if (current) return current;

        const newId = uuidv4();
        set({ anonymousId: newId });
        return newId;
      },

      setParticipation: (code, data) => {
        set((state) => ({
          participations: {
            ...state.participations,
            [code.toUpperCase()]: data,
          },
        }));
      },

      getParticipation: (code) => {
        return get().participations[code.toUpperCase()] || null;
      },

      clearParticipation: (code) => {
        set((state) => {
          const { [code.toUpperCase()]: _, ...rest } = state.participations;
          return { participations: rest };
        });
      },
    }),
    {
      name: 'storychain-anonymous',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
